import client from './client';
import type {
  LiveConn,
  ProxyConnectionAuditEvent,
  ProxyConnectionLeaderboardItem,
  ProxySpeedLeaderboardItem,
  ProxyTrafficLeaderboardItem,
  TrafficBucket,
} from '../types';
export const listProxies = (q: Record<string, any> = {}) =>
  client.get('/api/proxies', { params: q });
export interface ProxyRuntimeSummary {
  total: number;
  healthy: number;
  missing_child: number;
  creating: number;
  expired: number;
  deleted: number;
  active_connections: number;
}
export const getProxyRuntimeSummary = (params: Record<string, any> = {}) =>
  client.get<ProxyRuntimeSummary>('/api/proxies/runtime-summary', { params });
export interface CreateProxyBody {
  proxy_user_id: number;
  region_ids: number[];
  duration?: string;
  expire_at?: string;
  max_concurrent_clients?: number;
  node_id?: number;
  port?: number;
  note?: string;
  username?: string;
  password?: string;
}
// The server waits up to 10s for the agent ack; anything longer means the
// result is unknown rather than still pending.
export const CREATE_PROXY_TIMEOUT_MS = 30_000;
export const createProxy = (body: CreateProxyBody) =>
  client.post('/api/proxies', body, { timeout: CREATE_PROXY_TIMEOUT_MS });
export const patchProxyUserAssignment = (proxyID: number, proxyUserID: number | null) =>
  client.patch(`/api/proxies/${proxyID}`, { proxy_user_id: proxyUserID });
export const deleteProxy = (id: number) => client.delete(`/api/proxies/${id}`);
export const getProxyTraffic = (id: number, hours = 24, granularity: 'hour' | 'day' = 'hour') =>
  client.get<TrafficBucket[]>(`/api/proxies/${id}/traffic`, { params: { hours, granularity } });
export const getProxyConnections = (id: number) =>
  client.get<LiveConn[]>(`/api/proxies/${id}/connections`);
export interface AutoPotionRule {
  account: string; role_name: string; enabled: boolean;
  hp_threshold: number; hp_item_id: number; mp_threshold: number; mp_item_id: number;
}
export const getProxyAutoPotion = (id: number) =>
  client.get<{ rules: AutoPotionRule[] }>(`/api/proxies/${id}/auto-potion`);
export const saveProxyAutoPotion = (id: number, rule: AutoPotionRule) =>
  client.put(`/api/proxies/${id}/auto-potion`, rule);
export const testProxyAutoPotion = (id: number, body: { account: string; role_name: string; item_id: number; kind: 'hp' | 'mp' }) =>
  client.post(`/api/proxies/${id}/auto-potion/test`, body);
export const getConnectionLeaderboard = (limit = 20) =>
  client.get<{
    items: ProxyConnectionLeaderboardItem[];
    total: number;
    limit: number;
  }>(`/api/leaderboard/connections`, { params: { limit } });
export const getTrafficLeaderboard = (limit = 20) =>
  client.get<{
    items: ProxyTrafficLeaderboardItem[];
    total: number;
    limit: number;
  }>(`/api/leaderboard/traffic`, { params: { limit } });
export const getSpeedLeaderboard = (limit = 20) =>
  client.get<{
    items: ProxySpeedLeaderboardItem[];
    total: number;
    limit: number;
  }>(`/api/leaderboard/speed`, { params: { limit } });
// Filter shape for the audit drawer. `portMin`/`portMax` bracket
// target_port. The backend returns the echo so the drawer can
// display the active filter state in the toolbar.
export interface ConnectionAuditFilter {
  portMin?: number | null;
  portMax?: number | null;
  limit?: number;
  offset?: number;
}
export interface ConnectionAuditResponse {
  total: number;
  items: ProxyConnectionAuditEvent[];
  filters: {
    port_min?: number;
    port_max?: number;
  };
}
export const listProxyConnectionAudits = (id: number, filter: ConnectionAuditFilter = {}) => {
  const params: Record<string, any> = {};
  if (filter.portMin != null) params.port_min = filter.portMin;
  if (filter.portMax != null) params.port_max = filter.portMax;
  if (filter.limit != null) params.limit = filter.limit;
  if (filter.offset != null) params.offset = filter.offset;
  return client.get<ConnectionAuditResponse>(`/api/proxies/${id}/connection-audits`, { params });
};
export const updateProxyRegions = (id: number, regionIds: number[]) =>
  client.patch(`/api/proxies/${id}/regions`, { region_ids: regionIds });
// Edit the operator note (free-form, max 512 chars). Empty string
// clears the note (sets NULL). Used by ProxyDetailDrawer to let
// operators update the note after creation.
export const patchProxyNote = (id: number, note: string) =>
  client.patch(`/api/proxies/${id}`, { note });
// Run an SOCKS5 health probe through the agent. Optional body
// {"target": "host:port"} overrides the default target. The admin
// opens a TCP connection to the agent's SOCKS5 port, runs the
// user/pass handshake, and issues CONNECT through the proxy. RTT is
// broken down by stage (TCP / SOCKS5 handshake / CONNECT) so the
// operator can tell where latency is going.
export const healthcheckProxy = (id: number, target?: string) =>
  client.post<HealthcheckResult>(`/api/proxies/${id}/healthcheck`, target ? { target } : {});

export interface HealthcheckStep {
  step: string;
  ms: number;
}

export interface HealthcheckResult {
  ok: boolean;
  rtt_ms: number;
  steps: HealthcheckStep[];
  error?: string;
  proxy_id?: number;
  target: string;
  agent_ip: string;
  agent_port: number;
  detected_ipv4?: string;
  ip_location?: string;
  ipv4_check_ok: boolean;
  ipv4_error?: string;
  ip_source?: 'node' | string;
}

export interface BatchHealthcheckResult extends HealthcheckResult {
  proxy_id: number;
  username: string;
  node_name: string;
  status: 'available' | 'unavailable' | 'skipped' | string;
}

export interface BatchHealthcheckResponse {
  ok: boolean;
  total: number;
  available: number;
  unavailable: number;
  skipped: number;
  results: BatchHealthcheckResult[];
}

export const batchHealthcheckProxy = (proxyIds: number[], target?: string) =>
  client.post<BatchHealthcheckResponse>('/api/proxies/batch-healthcheck', {
    proxy_ids: proxyIds,
    ...(target ? { target } : {}),
  });
// Server-selection events captured by the agent's server_select
// scanner (0x0C 0x80 selection, 0x17 0x80 server_list). One row per
// player "I picked server #N line M" event. Used by ProxyDetailDrawer's
// "閫夋湇璁板綍" tab.
export interface ProxyServerSelectEvent {
  conn_id: number;
  source_ip: string;
  server_id: number;
  line_number: number;
  server_name: string;
  captured_at: string;
}
export const listProxyServerSelectEvents = (id: number, limit = 50) =>
  client.get<ProxyServerSelectEvent[]>(`/api/proxies/${id}/server-select-events`, { params: { limit } });
// Per-child control: kill the running child (SIGTERM) so the
// supervisor's child_watcher detects the death and the agent respawns
// a fresh child. Used to roll a stale-binary child forward after an
// agent upgrade is pushed 鈥?only affects the targeted proxy.
export const restartChild = (id: number) =>
  client.post(`/api/proxies/${id}/restart_child`);

// Extend the proxy's expire_at forward by an Nd / Nm string. The
// backend pushes expire_at to the running child over the control
// socket in real time so the customer keeps playing. Allowed only
// while status='active' 鈥?409 EXPIRED for the rest.
export const extendProxy = (id: number, duration: string) =>
  client.post(`/api/proxies/${id}/extend`, { duration });

// Move an active proxy's expiry backwards. The backend rejects any
// reduction that would leave the expiry at or before the current time.
export const reduceProxyTime = (id: number, duration: string) =>
  client.post(`/api/proxies/${id}/reduce-time`, { duration });

export interface TransferProxyTimeResponse {
  ok: boolean;
  source_proxy_id: number;
  target_proxy_id: number;
  transferred_seconds: number;
  target_expire_at: string;
  source_deleted: boolean;
  runtime_sync_warnings?: string[];
}

export const transferProxyTime = (sourceProxyID: number, targetProxyID: number) =>
  client.post<TransferProxyTimeResponse>(`/api/proxies/${sourceProxyID}/transfer-time`, {
    target_proxy_id: targetProxyID,
  });

// Per-row result for batch extend. Mirrors the backend's
// batchExtendResult. `status` is one of "updated" | "skipped" |
// "db_error"; on skip or db_error the `reason` field carries a
// short human-readable code (e.g. "EXPIRED", "NOT_FOUND",
// "STALE_PROXY_STATE", "AGENT_OFFLINE").
export interface BatchExtendResult {
  proxy_id: number;
  status: 'updated' | 'skipped' | 'db_error' | string;
  old_expire_at?: string;
  new_expire_at?: string;
  node_id?: number;
  reason?: string;
}

export interface BatchExtendResponse {
  ok: boolean;
  duration: string;
  updated: number;
  skipped: number;
  ws_failed: number;
  db_errors: number;
  results: BatchExtendResult[];
}

// Batch-extend many proxies in one request. The backend processes
// each row independently (partial success): 50 active + 5 expired
// returns `updated=50 skipped=5`, not 409. WS push is grouped by
// node_id so the wall-clock is bounded by the slowest single agent
// rather than the sum of per-row ack waits. Capped at 200 ids.
export const batchExtendProxy = (proxyIds: number[], duration: string) =>
  client.post<BatchExtendResponse>('/api/proxies/batch-extend', {
    proxy_ids: proxyIds,
    duration,
  });
